import { Card, H4, Spinner, SpinnerSize } from "@blueprintjs/core";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import css from "./charts.module.scss";

/**
 * Loading placeholder — keeps the page layout stable while scenario data
 * is streaming in (prevents content jumps as each chart finishes loading).
 */
export const ChartPlaceholder: React.FC<{
    title: string;
    message?: string;
}> = ({ title, message }) => (
    <Card className={css.chartCard}>
        <div className={css.chartHeader}>
            <H4 className={css.chartTitle}>{title}</H4>
        </div>
        <div className={css.chartPlaceholder}>
            <Spinner size={SpinnerSize.SMALL} />
            <div>{message ?? "Loading scenario data…"}</div>
        </div>
    </Card>
);

/**
 * Compact "not enough data" notice — used when scenario data has loaded
 * but a particular chart can't be drawn yet. Renders as a slim one-liner
 * instead of a full 360px chart card so it doesn't dominate the page.
 */
export const EmptyChartNotice: React.FC<{
    title?: string;
    message: string;
}> = ({ title, message }) => (
    <div className={css.emptyChartNotice}>
        {title !== undefined && <H4 className={css.chartTitle}>{title}</H4>}
        <span>{message}</span>
    </div>
);

/** Truncate a string with an ellipsis if it exceeds `max` characters. */
export const truncate = (s: string, max: number): string =>
    s.length <= max ? s : `${s.slice(0, max - 1)}…`;

/** Pick a stable display title for a scenario (falls back to short ID). */
export const getScenarioTitle = (s: ScenarioMetadata): string =>
    s.title || s.scenarioId.slice(0, 6);

/**
 * Rotated/truncated tick component for X-axis scenario labels. Anchors
 * at the end and rotates -35° so dozens of scenarios fit without
 * colliding.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ScenarioAxisTick: React.FC<{ [k: string]: any }> = (props) => {
    const { x, y, payload } = props as {
        x: number;
        y: number;
        payload: { value: string };
    };
    const txt = truncate(String(payload?.value ?? ""), 14);
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                dy={4}
                dx={-4}
                fontSize={11}
                textAnchor="end"
                transform="rotate(-35)"
                fill="#5c7080"
            >
                {txt}
            </text>
        </g>
    );
};
